import { TranscriptData, WordData } from '../data/chapters';

interface DisplayWord {
  element: HTMLElement;
  startTime: number;
  endTime: number;
  text: string;
}

export class WordSync {
  private container: HTMLElement;
  private words: DisplayWord[] = [];
  private currentWordIndex: number = -1;
  private visibleRange: { start: number; end: number } = { start: 0, end: 0 };
  private maxVisibleWords: number = 30;
  
  // Lerp-based smooth scrolling
  private targetScrollY: number = 0;
  private currentScrollY: number = 0;
  private lerpFactor: number = 0.04; // Very smooth (lower = smoother)
  private animationFrameId: number | null = null;
  
  constructor(container: HTMLElement) {
    this.container = container;
    this.startScrollAnimation();
  }
  
  private startScrollAnimation(): void {
    const animate = () => {
      // Lerp towards target
      this.currentScrollY += (this.targetScrollY - this.currentScrollY) * this.lerpFactor;
      
      // Apply transform (only if there's significant change)
      if (Math.abs(this.targetScrollY - this.currentScrollY) > 0.1) {
        this.container.style.transform = `translateY(${-this.currentScrollY}px)`;
      }
      
      this.animationFrameId = requestAnimationFrame(animate);
    };
    this.animationFrameId = requestAnimationFrame(animate);
  }
  
  private stopScrollAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
  
  public setTranscript(transcript: TranscriptData): void {
    this.clear();
    
    // Flatten all words from all segments
    const allWords: WordData[] = [];
    
    for (const segment of transcript.segments) {
      for (const word of segment.words) {
        // Skip whitespace-only words
        if (word.text.trim()) {
          allWords.push(word);
        }
      }
    }
    
    // Create word elements
    this.words = allWords.map((wordData) => {
      const element = document.createElement('span');
      element.className = 'word';
      element.textContent = wordData.text;
      this.container.appendChild(element);
      
      return {
        element,
        startTime: wordData.start_time,
        endTime: wordData.end_time,
        text: wordData.text
      };
    });
    
    // Initially show first batch of words
    this.updateVisibleRange(0);
  }
  
  public update(currentTime: number): void {
    if (this.words.length === 0) return;
    
    // Find current word
    let newCurrentIndex = -1;
    
    for (let i = 0; i < this.words.length; i++) {
      const word = this.words[i];
      
      if (currentTime >= word.startTime && currentTime <= word.endTime) {
        newCurrentIndex = i;
        break;
      } else if (currentTime > word.endTime && i === this.words.length - 1) {
        // Past the last word
        newCurrentIndex = i;
      } else if (currentTime > word.endTime && currentTime < this.words[i + 1]?.startTime) {
        // Between words - keep previous active
        newCurrentIndex = i;
      }
    }
    
    // Update visible range if needed
    if (newCurrentIndex >= 0) {
      this.updateVisibleRange(newCurrentIndex);
      this.updateScrollPosition(newCurrentIndex);
    }
    
    // Update word states
    for (let i = 0; i < this.words.length; i++) {
      const word = this.words[i];
      const element = word.element;
      
      // Remove all state classes
      element.classList.remove('active', 'past', 'visible');
      
      // Check if in visible range
      if (i >= this.visibleRange.start && i <= this.visibleRange.end) {
        element.classList.add('visible');
        
        if (i === newCurrentIndex) {
          element.classList.add('active');
        } else if (i < newCurrentIndex) {
          element.classList.add('past');
        }
      }
    }
    
    this.currentWordIndex = newCurrentIndex;
  }
  
  private updateScrollPosition(currentIndex: number): void {
    if (currentIndex < 0 || currentIndex >= this.words.length) return;
    
    const activeWord = this.words[currentIndex];
    if (!activeWord || !activeWord.element) return;
    
    // Get container and word positions
    const containerRect = this.container.parentElement?.getBoundingClientRect();
    const wordRect = activeWord.element.getBoundingClientRect();
    
    if (!containerRect) return;
    
    // Calculate how much to scroll to center the active word
    const containerCenter = containerRect.height / 2;
    const wordCenter = wordRect.top - containerRect.top + wordRect.height / 2 + this.currentScrollY;
    
    // Target: move word to center of container
    this.targetScrollY = wordCenter - containerCenter;
    
    // Clamp to reasonable bounds
    this.targetScrollY = Math.max(0, this.targetScrollY);
  }
  
  private updateVisibleRange(currentIndex: number): void {
    // Calculate how many words to show before and after current
    const wordsBefore = Math.floor(this.maxVisibleWords * 0.3);
    const wordsAfter = this.maxVisibleWords - wordsBefore;
    
    let start = Math.max(0, currentIndex - wordsBefore);
    let end = Math.min(this.words.length - 1, currentIndex + wordsAfter);
    
    // Adjust if we're at the beginning
    if (currentIndex < wordsBefore) {
      end = Math.min(this.words.length - 1, this.maxVisibleWords);
    }
    
    // Adjust if we're at the end
    if (currentIndex > this.words.length - wordsAfter) {
      start = Math.max(0, this.words.length - this.maxVisibleWords);
    }
    
    this.visibleRange = { start, end };
  }
  
  public clear(): void {
    this.container.innerHTML = '';
    this.words = [];
    this.currentWordIndex = -1;
    this.visibleRange = { start: 0, end: 0 };
    
    // Reset scroll position
    this.targetScrollY = 0;
    this.currentScrollY = 0;
    this.container.style.transform = 'translateY(0)';
  }
  
  public destroy(): void {
    this.stopScrollAnimation();
    this.clear();
  }
  
  public fadeOut(duration: number = 2000): Promise<void> {
    return new Promise((resolve) => {
      if (this.words.length === 0) {
        resolve();
        return;
      }
      
      // Add fade-out class to all visible words with staggered delay
      const visibleWords = this.words.filter((_, i) => 
        i >= this.visibleRange.start && i <= this.visibleRange.end
      );
      
      visibleWords.forEach((word, index) => {
        setTimeout(() => {
          word.element.classList.add('fading-out');
        }, index * 30); // Staggered fade
      });
      
      // Clear after animation completes
      setTimeout(() => {
        this.clear();
        resolve();
      }, duration);
    });
  }
  
  public getCurrentWord(): string | null {
    if (this.currentWordIndex >= 0 && this.currentWordIndex < this.words.length) {
      return this.words[this.currentWordIndex].text;
    }
    return null;
  }
}

